import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

const TRANSITION_DURATION = 300;

const LongTextInput = ({
  label,
  name,
  value = "",
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  readOnly = false,
  error,
  inputClassName = "",
}) => {
  const inputId = useId();
  const inputRef = useRef(null);
  const editorRef = useRef(null);
  const restoreFocusRef = useRef(false);
  const animationFrameRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState(null);

  const updatePosition = () => {
    const bounds = inputRef.current.getBoundingClientRect();
    const width = Math.min(Math.max(bounds.width, 400), window.innerWidth - 16);
    const above = bounds.top >= 160;
    setPosition({
      left: Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8)),
      width,
      above,
      ...(above
        ? {
            bottom: window.innerHeight - bounds.top + 8,
            top: "auto",
            maxHeight: bounds.top - 16,
          }
        : {
            top: bounds.bottom + 8,
            bottom: "auto",
            maxHeight: window.innerHeight - bounds.bottom - 16,
          }),
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      const timeout = window.setTimeout(
        () => setPosition(null),
        TRANSITION_DURATION,
      );
      return () => window.clearTimeout(timeout);
    }

    const selectionStart = inputRef.current.selectionStart;
    const selectionEnd = inputRef.current.selectionEnd;
    editorRef.current?.focus({ preventScroll: true });
    editorRef.current?.setSelectionRange(selectionStart, selectionEnd);
    animationFrameRef.current = window.requestAnimationFrame(() => {
      animationFrameRef.current = window.requestAnimationFrame(() =>
        setIsVisible(true),
      );
    });
    const reposition = event => {
      if (event.target !== editorRef.current) updatePosition();
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.cancelAnimationFrame(animationFrameRef.current);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [isOpen]);

  const closeEditor = event => {
    setIsOpen(false);
    onBlur?.(event);
  };

  const handleBlur = event => {
    if (
      event.target === inputRef.current ||
      event.relatedTarget === editorRef.current ||
      event.relatedTarget === inputRef.current
    )
      return;
    closeEditor(event);
  };

  const handleFocus = () => {
    if (restoreFocusRef.current) {
      restoreFocusRef.current = false;
      return;
    }
    if (disabled || readOnly) return;
    if (isOpen) {
      editorRef.current?.focus({ preventScroll: true });
      return;
    }
    updatePosition();
    setIsOpen(true);
  };

  const handleKeyDown = event => {
    if (event.key !== "Escape" && event.key !== "Tab") return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
    }
    // Return to the field before the browser performs normal Tab navigation.
    restoreFocusRef.current = true;
    inputRef.current.focus({ preventScroll: true });
    closeEditor(event);
  };

  const portalContainer =
    inputRef.current?.closest('[role="dialog"]') || document.body;

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {label && (
        <label
          htmlFor={inputId}
          className="mb-1 block text-xs font-medium text-black"
        >
          {label}
        </label>
      )}
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        name={name}
        aria-label={label || name}
        aria-expanded={isOpen}
        aria-controls={position ? `${inputId}-editor` : undefined}
        value={value ?? ""}
        onChange={onChange}
        onFocus={handleFocus}
        onClick={() => !isOpen && handleFocus()}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        className={`block w-full rounded border-black leading-5 focus:border-black focus:ring-0 disabled:border-gray-400 disabled:bg-white disabled:text-gray-400 ${inputClassName}`}
        style={{
          backgroundColor: isOpen ? "#dbeafe" : undefined,
          transition: `background-color ${TRANSITION_DURATION}ms ease`,
        }}
      />
      {position &&
        createPortal(
          <textarea
            ref={editorRef}
            id={`${inputId}-editor`}
            name={name}
            aria-label={`${label || name} editor`}
            aria-hidden={!isOpen}
            tabIndex={isOpen ? 0 : -1}
            value={value ?? ""}
            onChange={onChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onPointerDown={() =>
              editorRef.current?.focus({ preventScroll: true })
            }
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly || !isOpen}
            className="fixed z-[60] rounded border border-black bg-white p-3 text-sm text-black shadow-xl focus:border-black focus:ring-0"
            style={{
              backgroundColor: "#dbeafe",
              left: position.left,
              top: position.top,
              bottom: position.bottom,
              width: position.width,
              height: 160,
              minWidth: 160,
              minHeight: 80,
              maxWidth: window.innerWidth - position.left - 8,
              maxHeight: position.maxHeight,
              resize: "both",
              overflow: "auto",
              opacity: isVisible ? 1 : 0,
              transform: isVisible
                ? "translateY(0)"
                : `translateY(${position.above ? 8 : -8}px)`,
              transition: `opacity ${TRANSITION_DURATION}ms ease, transform ${TRANSITION_DURATION}ms ease`,
              pointerEvents: isOpen ? "auto" : "none",
            }}
          />,
          portalContainer,
        )}
      {error && <small className="text-sm text-red-600">{error}</small>}
    </div>
  );
};

export default LongTextInput;
